<template>
  <div id="app">
    <!-- App-level loading indicator -->
    <LoadingIndicator 
      :visible="isLoading" 
      :loading-text="''"
    />
    
    <div class="scene-container">
      <SceneViewer 
        ref="sceneViewer" 
        @loading-changed="handleLoadingChange"
      />
    </div>
    <div class="configurator-container">
      <KnobConfigurator 
        @knob-type-changed="handleKnobTypeChange" 
        @material-changed="handleMaterialChange"
        @enclosure-material-changed="handleEnclosureMaterialChange"
        @led-changed="handleLEDChange"
        :current-configuration="currentConfiguration"
        :is-loading="isLoading"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import SceneViewer from './components/SceneViewer.vue';
import KnobConfigurator from './components/Configurator.vue';
import LoadingIndicator from './components/LoadingIndicator.vue';
import { convertToDisplayConfiguration } from './utils/configurationHelper';

interface SceneViewerInstance {
  switchKnobType: (knobTypeId: string) => void;
  switchMaterial: (materialId: string) => void;
  switchEnclosureMaterial: (materialId: string) => void;
  switchLEDMaterial: (materialId: string) => void;
}

export default defineComponent({
  name: 'App', 
  components: {
    SceneViewer,
    KnobConfigurator,
    LoadingIndicator
  },
  setup() {
    const sceneViewer = ref<SceneViewerInstance | null>(null);
    
    // Configuration state
    const currentEnclosureMaterial = ref<string>('brushed_silver');
    const currentKnobType = ref<string>('classic');
    const currentKnobMaterial = ref<string>('classic_black');
    const currentLEDMaterial = ref<string>('led_red');

    // Computed configuration for checkout
    const currentConfiguration = computed(() => {
      return convertToDisplayConfiguration(
        currentEnclosureMaterial.value,
        currentKnobType.value,
        currentKnobMaterial.value,
        currentLEDMaterial.value
      );
    });

    const handleKnobTypeChange = (knobTypeId: string) => {
      console.log('App: Knob type changed to:', knobTypeId);
      currentKnobType.value = knobTypeId;
      if (sceneViewer.value) {
        sceneViewer.value.switchKnobType(knobTypeId);
      }
    };

    const handleMaterialChange = (materialId: string) => {
      console.log('App: Material changed to:', materialId);
      currentKnobMaterial.value = materialId;
      if (sceneViewer.value) {
        sceneViewer.value.switchMaterial(materialId);
      }
    };

    const handleEnclosureMaterialChange = (materialId: string) => {
      console.log('App: Enclosure material changed to:', materialId);
      currentEnclosureMaterial.value = materialId;
      if (sceneViewer.value) {
        sceneViewer.value.switchEnclosureMaterial(materialId);
      }
    };

    const handleLEDChange = (materialId: string) => {
      console.log('App: LED material changed to:', materialId);
      currentLEDMaterial.value = materialId;
      if (sceneViewer.value) {
        sceneViewer.value.switchLEDMaterial(materialId);
      }
    };

    // Start in loading state so the UI/scene can't "pop in" before Babylon can report status.
    const isLoading = ref<boolean>(true);

    const handleLoadingChange = (loading: boolean) => {
      console.log('App: Loading state changed to:', loading);
      isLoading.value = loading;
    };

    return {
      sceneViewer,
      currentConfiguration,
      handleKnobTypeChange,
      handleMaterialChange,
      handleEnclosureMaterialChange,
      handleLEDChange,
      isLoading,
      handleLoadingChange
    };
  }
});
</script>

<style>
html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  overflow: hidden;
  /* Match app/scene background (white) to avoid black bars on iPhone */
  background-color: rgb(255, 255, 255);
}

#app {
  font-family: "Inter", "Segoe UI", Arial, Helvetica, sans-serif;
  font-weight: 300;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  height: 100vh;
  display: flex;
  position: relative;
  background-color: rgb(255, 255, 255);
}

h2 {
  font-family: "Unica One", "Inter", "Segoe UI", Arial, Helvetica, sans-serif;
  font-weight: 400;
}

.scene-container {
  width: 66.67vw;  /* 2/3 of viewport width */
  height: 100vh;   /* Full viewport height */
}

.configurator-container {
  width: 33.33vw;
  height: 100vh;
  background-color: rgb(255, 255, 255);
}




/* Mobile responsive layout */
@media screen and (max-width: 768px) {
  #app {
    flex-direction: column;  /* Stack children vertically */
  }

  .scene-container {
    width: 100vw;
    height: 56.25vh; /* 45vh * 1.25 */
  }

  .configurator-container {
    width: 100vw;
    height: 43.75vh; /* remaining */
  }
}
</style>
